import { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, GripVertical, Smartphone } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../lib/api';

const fmt = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');

function Row({ v, slot }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: v.id });
  return (
    <div
      ref={setNodeRef}
      data-testid={`post-order-row-${v.id}`}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'var(--sf2)', border: '1.5px solid var(--bd)', marginBottom: 6, zIndex: isDragging ? 5 : 1, position: 'relative' }}
    >
      <span {...attributes} {...listeners} data-testid={`post-order-handle-${v.id}`} style={{ cursor: 'grab', color: 'var(--t3)', touchAction: 'none', display: 'grid', placeItems: 'center' }}><GripVertical size={16} /></span>
      <span style={{ width: 92, flexShrink: 0, fontFamily: 'Sora', fontWeight: 800, fontSize: 11.5, color: '#5BBEFC', textTransform: 'capitalize' }}>{fmt(slot)}</span>
      <span style={{ flex: 1, minWidth: 0, fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.publication_title || v.title || 'Sans titre'}</span>
    </div>
  );
}

const PRESETS = [['daily', 'Tous les jours', 1, 1], ['every2', 'Tous les 2 jours', 2, 1], ['twice', '2 par jour', 1, 2], ['custom', 'Personnalisé']];
const todayIso = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; };
const chip = (on) => ({ padding: '6px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5, background: on ? 'rgba(245,197,106,0.18)' : 'transparent', border: `1px solid ${on ? 'rgba(245,197,106,0.6)' : 'var(--bd)'}`, color: on ? '#F5C56A' : 'var(--t2)' });
const num = { width: 54, padding: '6px 8px', borderRadius: 8, background: 'var(--sf2)', border: '1px solid var(--bd)', color: 'var(--t1)', fontFamily: 'Sora', fontWeight: 700, fontSize: 12 };

// Bonus : preset de rythme mémorisé sur le projet + recalage des vidéos dans l'ordre affiché.
function RhythmPresets({ project, items, onSaved }) {
  const r = project.post_rhythm;
  const match = r && PRESETS.find((p) => p[2] === r.every_days && p[3] === r.per_day);
  const [preset, setPreset] = useState(r ? (match ? match[0] : 'custom') : null);
  const [every, setEvery] = useState(r?.every_days || 1);
  const [per, setPer] = useState(r?.per_day || 1);
  const [start, setStart] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const pick = (p) => { setPreset(p[0]); if (p[2]) { setEvery(p[2]); setPer(p[3]); } };
  const send = async (every_days) => {
    setBusy(true);
    try {
      const res = await api.post(`/projects/${project.id}/post-rhythm`, { every_days, per_day: per, start_date: start, video_ids: every_days ? items.map((x) => x.id) : [] });
      toast.success(every_days ? `Rythme appliqué à ${res.data.count} vidéo(s) ✓` : 'Rythme retiré');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); setBusy(false); }
  };
  return (
    <div data-testid="post-rhythm" style={{ marginTop: 16, padding: 14, borderRadius: 14, border: '1px dashed rgba(245,197,106,0.4)', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5, color: '#F5C56A' }}>Rythme de post {r ? '· actif' : ''}</span>
        {r && <button type="button" data-testid="post-rhythm-remove" disabled={busy} onClick={() => send(0)} style={{ ...chip(false), fontSize: 11 }}>Retirer</button>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {PRESETS.map((p) => <button key={p[0]} type="button" data-testid={`post-rhythm-${p[0]}`} onClick={() => pick(p)} style={chip(preset === p[0])}>{p[1]}</button>)}
      </div>
      {preset && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontFamily: 'Sora', fontSize: 12, color: 'var(--t2)' }}>
          {preset === 'custom' && (<>
            <input data-testid="post-rhythm-per" type="number" min={1} max={10} value={per} onChange={(e) => setPer(Math.max(1, +e.target.value || 1))} style={num} /> vidéo(s) tous les
            <input data-testid="post-rhythm-every" type="number" min={1} max={60} value={every} onChange={(e) => setEvery(Math.max(1, +e.target.value || 1))} style={num} /> jour(s),
          </>)}
          à partir du <input data-testid="post-rhythm-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} style={{ ...num, width: 'auto' }} />
        </div>
      )}
      {preset && <button type="button" data-testid="post-rhythm-apply" disabled={busy} onClick={() => send(every)} className="asa-btn asa-btn-ghost" style={{ width: '100%' }}>{busy ? 'Application…' : `Appliquer aux ${items.length} vidéo(s) dans cet ordre`}</button>}
      <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)' }}>Le rythme reste mémorisé : chaque nouvelle vidéo « À poster » se cale au créneau suivant.</div>
    </div>
  );
}

// Glisser-déposer l'ordre des vidéos « À poster » : les jours de post restent, les vidéos s'échangent les dates.
export default function PostOrderDialog({ project, tasks, onClose, onSaved }) {
  const initial = tasks
    .filter((t) => t.stage === 'to_post' && !t.archived)
    .sort((a, b) => (a.scheduled_post_date || '9999').localeCompare(b.scheduled_post_date || '9999'));
  const [items, setItems] = useState(initial);
  const [saving, setSaving] = useState(false);
  const slots = initial.map((t) => t.scheduled_post_date).sort((a, b) => (a || '9999').localeCompare(b || '9999'));
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setItems((xs) => arrayMove(xs, xs.findIndex((x) => x.id === active.id), xs.findIndex((x) => x.id === over.id)));
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${project.id}/post-order`, { video_ids: items.map((x) => x.id) });
      toast.success('Ordre des posts mis à jour ✓');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); setSaving(false); }
  };

  return createPortal(
    <div data-testid="post-order-dialog" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(6,12,26,0.78)', backdropFilter: 'blur(14px)', display: 'grid', placeItems: 'center', padding: 14 }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{ width: '100%', maxWidth: 620, maxHeight: '88vh', overflowY: 'auto', borderRadius: 20, padding: 20, background: 'linear-gradient(180deg, rgba(20,29,66,0.92) 0%, rgba(14,22,44,0.98) 100%)', border: '1px solid var(--bd)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Smartphone size={18} color="#5BBEFC" />
          <div style={{ flex: 1, fontFamily: 'Sora', fontWeight: 800, fontSize: 17, color: 'var(--t1)' }}>Ordre des posts · {project.name}</div>
          <button data-testid="post-order-close" onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--bd)', borderRadius: 9, padding: 7, color: 'var(--t2)', cursor: 'pointer' }}><X size={14} /></button>
        </div>
        <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginBottom: 14 }}>Glisse les vidéos dans l&apos;ordre voulu : les jours de post ne bougent pas, ce sont les vidéos qui s&apos;échangent les dates.</div>
        {items.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--t3)', fontFamily: 'Sora', fontSize: 13 }}>Aucune vidéo « À poster » dans ce projet.</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map((x) => x.id)} strategy={verticalListSortingStrategy}>
              {items.map((v, i) => <Row key={v.id} v={v} slot={slots[i]} />)}
            </SortableContext>
          </DndContext>
        )}
        <button data-testid="post-order-save" onClick={save} disabled={saving || !items.length} className="asa-btn asa-btn-primary" style={{ width: '100%', marginTop: 12 }}>{saving ? 'Enregistrement…' : 'Enregistrer l\'ordre'}</button>
        <RhythmPresets project={project} items={items} onSaved={onSaved} />
      </div>
    </div>,
    document.body,
  );
}
